<script lang="ts">
	import LazyBackgroundImage from "$lib/ui/shared/LazyBackgroundImage.svelte";
	import { formatCurrency } from "$lib/util/format";
	import type { Item } from "proto/menu";

	export let item: Item;

	$: hasImage = !!item.media;
	$: isBOGOF = "menu_deal_action" in item && item.menu_deal_action === "buy_1_get_1_free";
	$: isSoldOut = item.availability?.status === "sold_out";

	const getDisplayPrice = (i: Item) => {
		// TODO: Double-check logic with Pieter
		// TODO: Once okay, move to restaurant service file + discuss changing the implementationt that is on prod
		// 1. Check `menu_deal_action` prop is defined && === "discount"
		// 		1.1. NO: return: {
		// 			now: price of cheapest variant
		// 		}
		// 		1.2. YES: get the list of variants on promotion (look at their `menu_deal_action` property, if exists)
		//			Is there a variant whose promo price is strictly lower than the others?
		// 			1.2.1. YES: return {
		// 			 	was: that variant's old price
		//			 	now: that variant's price
		// 		 	}
		// 			1.2.2. NO: return {
		// 				was: 1st of the variants with lower promo price - its old price
		// 				now: 1st of the variants with lower promo price - its promo price
		// 			}

		if ("menu_deal_action" in i && i.menu_deal_action == "discount") {
			const variantsOnPromotion = i.variants.filter(
				(variant) => "menu_deal_action" in variant && variant.menu_deal_action === "discount",
			);
			// Sort from smallest promo price to largest
			variantsOnPromotion.sort((variantA, variantB) => {
				const promoPriceA = variantA.price.toFixed(2);
				const promoPriceB = variantB.price.toFixed(2);

				if (promoPriceA > promoPriceB) return 1;
				if (promoPriceA < promoPriceB) return -1;
				// otherwise, promoPriceA and promoPriceB are identical.  In which case, don't change their
				// order (we need to use the order of the variants in order to decide which old price to
				// show)
				return 0;
			});

			const firstCheapestPromo = variantsOnPromotion[0];
			return {
				"was": firstCheapestPromo.old_price,
				"now": firstCheapestPromo.price,
			};
		} else {
			const lowestVariantPrice: number | undefined = i.variants.reduce((min, variant) => {
				return Math.min(min, variant.price);
			}, Number.MAX_SAFE_INTEGER);

			return {
				"now": lowestVariantPrice,
			}
		}
	};
</script>

<LazyBackgroundImage image={[item.media, 300]} alt={item.name}>
	{#if isBOGOF && hasImage}
		<span class="bogof-tag bold label-s top-left" class:sold-out={isSoldOut} />
		<!-- TODO: Ask Pieter if only for BOGOF or any other promotions e.g. discount (but is covered by wasnow price label?)-->
		<!-- TODO: Can only a variant have BOGOF, not whole item? -->
	{/if}
	{#if !hasImage && item.description}
		<div class={`description label-s book ${isSoldOut ? "low" : "medium"}-importance`}>
			<span>{item.description}</span>
		</div>
	{/if}

	{#if isSoldOut}
		<span class="label-s bold sold-out-tag">Sold out</span>
	{:else}
		<div class="quantity">
			<button>
				<span class="body-l bold add"><span>Add</span></span>
				<span class="icon-add-28 vertical-middle"></span>
			</button>
		</div>
	{/if}
</LazyBackgroundImage>
<div class="fill-space">
	{#if item.variants.length}
		{@const { was: wasPrice, now: price } = getDisplayPrice(item)}
		<p class="label-xl m-0" class:sold-out={isSoldOut}>
			{#if !wasPrice}
				<!-- TODO: Ask Pieter re. BOGOF price (also red + without "from"?) -->
				<span class="label-l book medium-importance" class:sold-out={isSoldOut}>from</span>
				<span class="bold">{formatCurrency(price)}</span>
			{:else}
				<span class="bold deal-price" class:sold-out={isSoldOut}>{formatCurrency(price)}</span>
				<span class="book medium-importance strike-through" class:sold-out={isSoldOut}>{formatCurrency(wasPrice)}</span>
			{/if}
		</p>
	{/if}
	{#if !hasImage && isBOGOF}
		<span class="bogof-tag bold label-s mt-0_25 mb-0" class:sold-out={isSoldOut} />
	{/if}
	<div class="title-container" class:sold-out={isSoldOut}>
		<h4 class="title label-xl book">{item.name}</h4>
	</div>
	{#if item.collect_only}
		<span class="collect-only pill small label-s bold mt-0_25">Collect only</span>
	{/if}
</div>

<style lang="scss">
	@use "color";
	@use "layout";
	@use "screen";
	@use "padding";

	// TODO: Integrate properly with styles across app
	div.title-container {
		// FEW-1217: Limit item title to 4 lines
		@include layout.line-clamping(4);
	}

	.mt-0_25 { margin-top: 0.25rem; }
	.mb-0 { margin-bottom: 0; }

	span.collect-only {
		// FEW-1218: "Collect only" pill
		// TODO: Ask Pieter if this style is changing across app
		background-color: color.$object-elevation-6dp;
		border: 0;
		margin-left: 0;
	}

	.deal-price {
		// FEW-1219: Was/Now price
		// TODO: Confirm wasnow dark-mode colour (Pricing component vs design on POC doc)
		color: color.$wasnow;
		margin-right: 0.4rem;
	}

	// FEW-1222: Sold out items
	span.sold-out-tag {
		position: absolute;
		bottom: 6px;
		right: 6px;

		background-color: color.$white;
		color: color.$black;
		border: 1px solid color.$separator;
		box-shadow: layout.$box-shadow;
		border-radius: layout.$border-radius;
		padding: var(--tag-padding);
	}

	.sold-out {
		color: color.$on-background-low-importance;
	}

	div.description {
		// FEW-1223: Items without an image (show description)
		--num-lines: 3;
		@media (screen.$md) { --num-lines: 4; }

		margin: 0;
		@include padding.x-small;

		> span {
			// Limit item description to 3 or 4 lines (mobile / desktop)
			@include layout.line-clamping(var(--num-lines));
		}
	}

	.bogof-tag {
		// FEW-1232 & FEW-1220: BOGOF tag
		&:after { content: "Buy 1 Get 1 Free"; }

		background-color: color.$red-2;
		color: color.$white;
		padding: var(--tag-padding);
		border-radius: layout.$border-radius;
		// Snap the box to fit the text, not the whole width of the parent container
		width: fit-content;
		
		@include layout.line-clamping(1);

		&.sold-out {
			background-color: color.$object-elevation-6dp;
			color: color.$on-background-high-importance;
		}

		// When we want the tag to appear in the top-left corner of the item's image
		&.top-left {
			--offset: 6px;
			top: var(--offset);
			left: var(--offset);
			margin: 0;

			position: absolute;
			z-index: 1;

			// * Limit max width because `position: absolute` means parent does not control this anymore
			// * The `2 * offset` is for the horizontal margins (left + right)
			max-width: calc(100% - 2 * var(--offset));

			&.sold-out {
				background-color: color.$promo-sold-out;
				color: color.$black;
			}
		}
	}
</style>